/**
 * OrderValidationTab — Suggested replenishment orders grouped by Store.
 * Each store shows a collapsible summary row; expanding reveals SKU detail lines.
 * All lines are editable (suggested qty) and have approve/reject buttons.
 * Filter-reactive via shared ReplenishmentFilterContext.
 *
 * Column groups (header colour coded):
 *   Identity:  Brand | SKU | Description | Item Status | ABC Class
 *   Stock:     Target Stock | Current Stock | In Transit | Avg Sales
 *   Order:     Suggested Qty (editable) | Value | Order Status
 */
import React, { useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Tooltip, Chip, IconButton,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import ExportMenu from '../Common/ExportMenu';
import { useReplenishmentFilters } from './ReplenishmentFilterContext';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

/* ---------- types ---------- */

interface OrderLine {
  order_id: string;
  store_id: string;
  store_name: string;
  brand: string;
  sku: string;
  product_name: string;
  item_status: string | null;
  abc_class: string | null;
  target_stock: number;
  current_stock: number;
  in_transit: number;
  avg_daily_sales: number;
  suggested_quantity: number;
  unit_cost: number;
  value: number;
  status: string;
}

interface ValidationResponse { lines: OrderLine[]; total: number; }

interface StoreGroup {
  store_id: string;
  store_name: string;
  lines: OrderLine[];
  totalQty: number;
  totalValue: number;
  totalTarget: number;
  totalCurrent: number;
  totalTransit: number;
  totalAvgSales: number;
  skuCount: number;
  pendingCount: number;
}

/* ---------- header colours ---------- */

const HDR_ID    = '#F8F6F3';
const HDR_STK   = '#EDF2F7';
const HDR_ORD   = '#FDF2F0';
const BDR_ID    = '#E8E3DC';
const BDR_STK   = '#D5DFE9';
const BDR_ORD   = '#F0D9D4';
const SEP_ID    = '#D5D0C8';
const SEP_STK   = '#B8C8D8';

/* ---------- ABC Chip style (matches ProjectionGridTab / SKU Projection) ---------- */

const ABC_BG: Record<string, string> = { A: '#e8f5e9', B: '#e3f2fd', C: '#fff3e0', D: '#fce4ec', E: '#f5f5f5' };
const ABC_FG: Record<string, string> = { A: '#2e7d32', B: '#1565c0', C: '#e65100', D: '#c62828', E: '#616161' };

/* ---------- helpers ---------- */

const fmtN = (v: number) => v.toLocaleString();

const statusLabel = (s: string) => {
  switch (s?.toUpperCase()) {
    case 'APPROVED': return 'Approved';
    case 'TRANSMITTED': return 'Ongoing';
    case 'REJECTED': return 'Rejected';
    default: return 'Pending';
  }
};

/* column count for colSpan */
const COL_COUNT = 12;

/* compact cell padding */
const cPx = '6px';
const cPy = '4px';

/* ---------- shared header style ---------- */
const hdr = (bg: string, bdr: string, extra?: object) => ({
  fontWeight: 700, fontSize: '9px', textTransform: 'uppercase' as const,
  letterSpacing: '0.05em', bgcolor: bg, borderBottom: `2px solid ${bdr}`,
  whiteSpace: 'nowrap' as const, py: '7px', px: cPx, ...extra,
});

/* ---------- component ---------- */

const OrderValidationTab: React.FC = () => {
  const { brands, categories, stores, cities } = useReplenishmentFilters();
  const { fmtCurrencyCompact, fmtCurrencyLabel } = useCurrencyFormat();
  const queryClient = useQueryClient();

  const [qtyOverrides, setQtyOverrides] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { data, isLoading } = useQuery<ValidationResponse>({
    queryKey: ['order-validation-lines', brands, categories, stores, cities],
    queryFn: () =>
      api.getOrderValidationLines({
        brands: brands.length > 0 ? brands.join(',') : undefined,
        categories: categories.length > 0 ? categories.join(',') : undefined,
        cities: cities.length > 0 ? cities.join(',') : undefined,
        stores: stores.length > 0 ? stores.join(',') : undefined,
      }),
    refetchInterval: 300000,
    retry: 1,
  });

  const lines = useMemo(() => data?.lines || [], [data]);

  const getQty = useCallback(
    (l: OrderLine): number => {
      const raw = qtyOverrides[l.order_id];
      if (raw === undefined) return l.suggested_quantity;
      const parsed = parseInt(raw, 10);
      return isNaN(parsed) ? 0 : parsed;
    },
    [qtyOverrides],
  );
  const getDisplayQty = useCallback(
    (l: OrderLine): string | number => {
      const raw = qtyOverrides[l.order_id];
      return raw !== undefined ? raw : l.suggested_quantity;
    },
    [qtyOverrides],
  );
  const getVal = useCallback((l: OrderLine) => getQty(l) * l.unit_cost, [getQty]);

  /* ---- group by store ---- */
  const storeGroups: StoreGroup[] = useMemo(() => {
    const map: Record<string, StoreGroup> = {};
    for (const l of lines) {
      if (!map[l.store_id]) {
        map[l.store_id] = { store_id: l.store_id, store_name: l.store_name, lines: [], totalQty: 0, totalValue: 0, totalTarget: 0, totalCurrent: 0, totalTransit: 0, totalAvgSales: 0, skuCount: 0, pendingCount: 0 };
      }
      const g = map[l.store_id];
      g.lines.push(l);
      g.totalQty += getQty(l);
      g.totalValue += getVal(l);
      g.totalTarget += l.target_stock;
      g.totalCurrent += l.current_stock;
      g.totalTransit += l.in_transit;
      g.totalAvgSales += l.avg_daily_sales;
      g.skuCount++;
      if (['PENDING', 'TRANSMITTED'].includes(l.status?.toUpperCase())) g.pendingCount++;
    }
    return Object.values(map).sort((a, b) => a.store_name.localeCompare(b.store_name));
  }, [lines, getQty, getVal]);

  /* ---- KPIs ---- */
  const totals = useMemo(() => {
    const totalLines = lines.length;
    const totalQty = lines.reduce((s, l) => s + getQty(l), 0);
    const totalValue = lines.reduce((s, l) => s + getVal(l), 0);
    const pendingCount = lines.filter((l) => ['PENDING', 'TRANSMITTED'].includes(l.status?.toUpperCase())).length;
    return { totalLines, totalQty, totalValue, pendingCount };
  }, [lines, getQty, getVal]);

  /* ---- mutations ---- */
  const approveMutation = useMutation({
    mutationFn: (d: { orderId: string; quantity: number }) => api.approveOrder(d.orderId, d.quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-validation-lines'] });
      // Validated orders move to Order History — refresh that view too.
      queryClient.invalidateQueries({ queryKey: ['orderHistory'] });
    },
  });
  const rejectMutation = useMutation({
    mutationFn: (orderId: string) => api.rejectOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-validation-lines'] });
      queryClient.invalidateQueries({ queryKey: ['orderHistory'] });
    },
  });

  const handleApprove = useCallback((l: OrderLine) => approveMutation.mutate({ orderId: l.order_id, quantity: getQty(l) }), [approveMutation, getQty]);
  const handleReject = useCallback((l: OrderLine) => rejectMutation.mutate(l.order_id), [rejectMutation]);

  /* bulk approve all pending in a store */
  const handleApproveStore = useCallback((group: StoreGroup) => {
    group.lines
      .filter((l) => ['PENDING', 'TRANSMITTED'].includes(l.status?.toUpperCase()))
      .forEach((l) => approveMutation.mutate({ orderId: l.order_id, quantity: getQty(l) }));
  }, [approveMutation, getQty]);

  /* bulk reject all pending in a store — all rejected lines for one
     store on one day land on the same PO (via the backend's
     get-or-create PO logic), so Order History shows one cancelled PO
     row with N lines inside rather than N separate rows. */
  const handleRejectStore = useCallback((group: StoreGroup) => {
    group.lines
      .filter((l) => ['PENDING', 'TRANSMITTED'].includes(l.status?.toUpperCase()))
      .forEach((l) => rejectMutation.mutate(l.order_id));
  }, [rejectMutation]);

  const handleQtyChange = useCallback((orderId: string, raw: string) => {
    // Allow empty string so the field can be fully cleared, plus any
    // non-negative integer. Reject anything else (letters, negatives, etc.).
    if (raw === '' || /^\d+$/.test(raw)) {
      setQtyOverrides((prev) => ({ ...prev, [orderId]: raw }));
    }
  }, []);

  const toggleExpand = useCallback((storeId: string) => {
    setExpanded((prev) => ({ ...prev, [storeId]: !prev[storeId] }));
  }, []);

  /* ---- export rows ---- */
  const exportRows = useMemo(() => {
    if (!lines.length) return [];
    return lines.map((l) => ({
      Store: l.store_name, Brand: l.brand, SKU: l.sku, Description: l.product_name,
      'Item Status': l.item_status || '—', 'ABC Class': l.abc_class || '—',
      'Target Stock': l.target_stock, 'Current Stock': l.current_stock,
      'In Transit': l.in_transit, 'Avg Sales': Math.round(l.avg_daily_sales),
      'Suggested Qty': l.suggested_quantity, 'Adjusted Qty': getQty(l),
      [`Value ${fmtCurrencyLabel()}`]: Math.round(getVal(l)),
      Status: statusLabel(l.status),
    }));
  }, [lines, getQty, getVal, fmtCurrencyLabel]);

  /* ---- action buttons ----
   * `onReject` is optional — store-level rows pass only an approve
   * handler so the dangerous "reject every pending line for this
   * store" bulk action is no longer a single click. Line-level rows
   * still expose both approve and reject so rejections are always
   * explicit, one SKU at a time.
   */
  const ActionButtons: React.FC<{ onApprove: () => void; onReject?: () => void; loading?: boolean }> = ({ onApprove, onReject, loading }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
      <Tooltip title="Approve">
        <Button size="small" variant="contained" color="success" onClick={onApprove} disabled={loading}
          sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: '9px', fontWeight: 700, textTransform: 'none', lineHeight: 1.4 }}
          startIcon={<ApproveIcon sx={{ fontSize: '13px !important' }} />}>
          Approve
        </Button>
      </Tooltip>
      {onReject && (
        <Tooltip title="Reject">
          <Button size="small" variant="outlined" color="error" onClick={onReject} disabled={loading}
            sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: '9px', fontWeight: 700, textTransform: 'none', lineHeight: 1.4 }}
            startIcon={<RejectIcon sx={{ fontSize: '13px !important' }} />}>
            Reject
          </Button>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box>
      {/* ── Export ── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <ExportMenu
          data={exportRows}
          filename="Order_Validation"
          title="Order Validation"
          sheetName="Orders"
        />
      </Box>

      {/* ── KPI Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Order Lines', value: fmtN(totals.totalLines) },
          { label: 'Total Suggested Qty', value: fmtN(totals.totalQty) },
          { label: 'Total Value', value: fmtCurrencyCompact(totals.totalValue) },
          { label: 'Pending Approval', value: fmtN(totals.pendingCount) },
        ].map((kpi) => (
          <Grid item xs={6} md={3} key={kpi.label}>
            <Paper sx={{ p: '14px 18px', border: '1px solid var(--color-border)' }}>
              <Typography sx={{ fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-text-muted)', mb: 0.5 }}>
                {kpi.label}
              </Typography>
              <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {isLoading ? <CircularProgress size={16} /> : kpi.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Table ── */}
      <Paper sx={{ border: '1px solid var(--color-border)', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>

            {/* Column widths — do NOT put JSX comments between the
                <col /> tags. The whitespace around inline JSX comments
                inside <colgroup> produces text nodes that React warns
                about in development (validateDOMNesting). */}
            <colgroup>
              <col style={{ width: 36 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 68 }} />
              <col style={{ width: 48 }} />
              <col style={{ width: 60 }} />
              <col style={{ width: 60 }} />
              <col style={{ width: 55 }} />
              <col style={{ width: 55 }} />
              <col style={{ width: 78 }} />
              <col style={{ width: 68 }} />
              <col style={{ width: 140 }} />
            </colgroup>

            <TableHead>
              <TableRow>
                {/* identity */}
                <TableCell sx={hdr(HDR_ID, BDR_ID, { position: 'sticky', left: 0, zIndex: 3 })}></TableCell>
                <TableCell sx={hdr(HDR_ID, BDR_ID)}>Brand</TableCell>
                <TableCell sx={hdr(HDR_ID, BDR_ID)}>SKU</TableCell>
                <TableCell sx={hdr(HDR_ID, BDR_ID)}>Description</TableCell>
                <TableCell sx={hdr(HDR_ID, BDR_ID)}>Item Status</TableCell>
                <TableCell sx={hdr(HDR_ID, BDR_ID, { borderRight: `2px solid ${SEP_ID}` })}>ABC</TableCell>
                {/* stock */}
                <TableCell align="right" sx={hdr(HDR_STK, BDR_STK)}>Target</TableCell>
                <TableCell align="right" sx={hdr(HDR_STK, BDR_STK)}>Stock</TableCell>
                <TableCell align="right" sx={hdr(HDR_STK, BDR_STK)}>Transit</TableCell>
                <TableCell align="right" sx={hdr(HDR_STK, BDR_STK, { borderRight: `2px solid ${SEP_STK}` })}>Avg Sales</TableCell>
                {/* order */}
                <TableCell align="right" sx={hdr(HDR_ORD, BDR_ORD)}>Qty</TableCell>
                <TableCell align="right" sx={hdr(HDR_ORD, BDR_ORD)}>Value</TableCell>
                <TableCell align="center" sx={hdr(HDR_ORD, BDR_ORD)}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}><TableCell colSpan={COL_COUNT + 1}><Box className="skeleton-shimmer" sx={{ height: 14, borderRadius: '4px' }} /></TableCell></TableRow>
                ))
              ) : storeGroups.length > 0 ? (
                storeGroups.map((group) => {
                  const isOpen = !!expanded[group.store_id];
                  return (
                    <React.Fragment key={group.store_id}>
                      {/* ── Store summary row ── */}
                      <TableRow
                        onClick={() => toggleExpand(group.store_id)}
                        sx={{ cursor: 'pointer', bgcolor: '#FAFAF8', '&:hover': { bgcolor: '#F5F4F1' } }}
                      >
                        <TableCell sx={{ py: cPy, px: '4px', position: 'sticky', left: 0, bgcolor: 'inherit', zIndex: 1 }}>
                          <IconButton size="small" sx={{ p: 0.25 }}>
                            {isOpen ? <KeyboardArrowDown sx={{ fontSize: 16 }} /> : <KeyboardArrowRight sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </TableCell>
                        <TableCell colSpan={5} sx={{ py: cPy, px: cPx, fontWeight: 700, fontSize: '11px', borderRight: `2px solid ${SEP_ID}` }}>
                          {group.store_name}
                          <Typography component="span" sx={{ ml: 1, fontSize: '9px', color: 'var(--color-text-muted)', fontWeight: 400 }}>
                            {group.skuCount} SKU{group.skuCount !== 1 ? 's' : ''}
                            {group.pendingCount > 0 && <> &middot; {group.pendingCount} pending</>}
                          </Typography>
                        </TableCell>
                        {/* stock totals */}
                        <TableCell align="right" sx={{ py: cPy, px: cPx, fontWeight: 600, fontSize: '10px', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>
                          {fmtN(group.totalTarget)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: cPy, px: cPx, fontWeight: 600, fontSize: '10px', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>
                          {fmtN(group.totalCurrent)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: cPy, px: cPx, fontWeight: 600, fontSize: '10px', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>
                          {fmtN(group.totalTransit)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: cPy, px: cPx, fontWeight: 600, fontSize: '10px', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)', borderRight: `2px solid ${SEP_STK}` }}>
                          {Math.round(group.totalAvgSales)}
                        </TableCell>
                        {/* order summary */}
                        <TableCell align="right" sx={{ py: cPy, px: cPx, fontWeight: 700, fontSize: '11px', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtN(group.totalQty)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: cPy, px: cPx, fontWeight: 700, fontSize: '11px', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtCurrencyCompact(group.totalValue)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: cPy, px: '4px' }} onClick={(e) => e.stopPropagation()}>
                          <ActionButtons
                            onApprove={() => handleApproveStore(group)}
                            onReject={() => handleRejectStore(group)}
                            loading={approveMutation.isPending || rejectMutation.isPending}
                          />
                        </TableCell>
                      </TableRow>

                      {/* ── Expanded SKU rows ── */}
                      {isOpen && group.lines.map((line) => {
                        const effQty = getQty(line);
                        const displayQty = getDisplayQty(line);
                        const isOverridden = line.order_id in qtyOverrides && qtyOverrides[line.order_id] !== String(line.suggested_quantity);
                        const effValue = effQty * line.unit_cost;
                        return (
                          <TableRow key={line.order_id} hover sx={{ bgcolor: '#fff' }}>
                            {/* indent */}
                            <TableCell sx={{ py: cPy, px: '4px', position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }} />

                            <TableCell sx={{ py: cPy, px: cPx, fontSize: '10px', color: 'var(--color-text-muted)' }}>
                              {line.brand}
                            </TableCell>
                            <TableCell sx={{ py: cPy, px: cPx, fontSize: '10px', fontWeight: 500 }}>
                              {line.sku}
                            </TableCell>
                            <TableCell sx={{ py: cPy, px: cPx, fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <Tooltip title={line.product_name} placement="top-start">
                                <span>{line.product_name}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ py: cPy, px: cPx, fontSize: '9px' }}>
                              {line.item_status ? (
                                <Chip
                                  label={line.item_status}
                                  size="small"
                                  color={
                                    line.item_status === 'NEW' ? 'info' :
                                    line.item_status === 'GROWTH' ? 'success' :
                                    line.item_status === 'DECLINING' ? 'warning' :
                                    (line.item_status === 'PHASE_OUT' || line.item_status === 'DISCONTINUED') ? 'error' :
                                    'default'
                                  }
                                  variant="outlined"
                                  sx={{ fontSize: '10px', height: 20 }}
                                />
                              ) : '—'}
                            </TableCell>
                            <TableCell sx={{ py: cPy, px: cPx, borderRight: `2px solid ${SEP_ID}` }}>
                              {line.abc_class ? (
                                <Chip label={line.abc_class} size="small" variant="filled"
                                  sx={{
                                    fontSize: '11px', height: 20, fontWeight: 700,
                                    bgcolor: ABC_BG[line.abc_class] || '#f5f5f5',
                                    color: ABC_FG[line.abc_class] || '#616161',
                                  }} />
                              ) : '—'}
                            </TableCell>

                            {/* stock */}
                            <TableCell align="right" sx={{ py: cPy, px: cPx, fontSize: '10px', fontVariantNumeric: 'tabular-nums', bgcolor: 'rgba(237,242,247,0.15)' }}>
                              {fmtN(line.target_stock)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: cPy, px: cPx, fontSize: '10px', fontVariantNumeric: 'tabular-nums', bgcolor: 'rgba(237,242,247,0.15)' }}>
                              {fmtN(line.current_stock)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: cPy, px: cPx, fontSize: '10px', fontVariantNumeric: 'tabular-nums', bgcolor: 'rgba(237,242,247,0.15)' }}>
                              {fmtN(line.in_transit)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: cPy, px: cPx, fontSize: '10px', fontVariantNumeric: 'tabular-nums', bgcolor: 'rgba(237,242,247,0.15)', borderRight: `2px solid ${SEP_STK}` }}>
                              {Math.round(line.avg_daily_sales)}
                            </TableCell>

                            {/* order — always editable + buttons */}
                            <TableCell align="right" sx={{ py: '2px', px: '3px', bgcolor: 'rgba(253,242,240,0.18)' }}>
                              <TextField
                                type="number" size="small" value={displayQty}
                                onChange={(e) => handleQtyChange(line.order_id, e.target.value)}
                                inputProps={{ min: 0, style: { textAlign: 'right', fontSize: '10px', padding: '3px 5px', fontVariantNumeric: 'tabular-nums' } }}
                                sx={{
                                  width: 68,
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: isOverridden ? '#722F37' : 'var(--color-border)', borderWidth: isOverridden ? 2 : 1 },
                                    '&:hover fieldset': { borderColor: '#722F37' },
                                    '&.Mui-focused fieldset': { borderColor: '#722F37' },
                                  },
                                  bgcolor: isOverridden ? 'rgba(114,47,55,0.04)' : 'transparent',
                                }}
                              />
                              {isOverridden && (
                                <Typography sx={{ fontSize: '7px', color: '#722F37', mt: 0.15, textAlign: 'right' }}>
                                  was {fmtN(line.suggested_quantity)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ py: cPy, px: cPx, fontSize: '10px', fontWeight: 500, fontVariantNumeric: 'tabular-nums', bgcolor: 'rgba(253,242,240,0.18)' }}>
                              {fmtCurrencyCompact(effValue)}
                            </TableCell>
                            <TableCell align="center" sx={{ py: cPy, px: '3px', bgcolor: 'rgba(253,242,240,0.18)' }}>
                              <ActionButtons
                                onApprove={() => handleApprove(line)}
                                onReject={() => handleReject(line)}
                                loading={approveMutation.isPending || rejectMutation.isPending}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={COL_COUNT + 1}>
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)', mb: 0.5 }}>
                        No pending orders
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                        Generate orders from the Order Suggestions tab, then come back here to review and approve
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {lines.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '6px 16px', borderTop: '1px solid var(--color-border)' }}>
            <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>
              {storeGroups.length} store{storeGroups.length !== 1 ? 's' : ''} &middot; {lines.length} line{lines.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OrderValidationTab;
